/**
 * Format integer cents as a USD dollar string.
 * @example formatPrice(525) === "$5.25"
 */
export function formatPrice(cents) {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(cents / 100);
}
/**
 * Format a sequential order number with leading zeros, prefixed by "#".
 * @example formatOrderNumber(42) === "#0042"
 */
export function formatOrderNumber(orderNumber) {
    return `#${String(orderNumber).padStart(4, '0')}`;
}
/**
 * Compute the line total in cents for a single order item.
 * `(basePrice + sum of customization deltas) * quantity`.
 */
export function computeLineTotal(basePriceCents, selectedCustomizations, quantity) {
    const deltas = selectedCustomizations.reduce((sum, sel) => sum + sel.priceDeltaCents, 0);
    return (basePriceCents + deltas) * quantity;
}
/** Sum of `lineTotalCents` across all order items. */
export function computeSubtotal(items) {
    return items.reduce((sum, item) => sum + item.lineTotalCents, 0);
}
/** Tax in cents, rounded to the nearest cent. */
export function computeTax(subtotalCents, taxRate) {
    return Math.round(subtotalCents * taxRate);
}
/** Total cents = subtotal + tax + tip. */
export function computeTotal(subtotalCents, taxCents, tipCents) {
    return subtotalCents + taxCents + tipCents;
}
/** Tip cents from a percentage (e.g., 0.15 for 15%), rounded to nearest cent. */
export function computePercentageTip(subtotalCents, pct) {
    return Math.round(subtotalCents * pct);
}
/**
 * Format the elapsed time since an ISO timestamp as "Xm YYs".
 * Used by the kitchen display.
 */
export function formatElapsed(fromIso, now = new Date()) {
    const elapsedMs = now.getTime() - new Date(fromIso).getTime();
    const totalSec = Math.max(0, Math.floor(elapsedMs / 1000));
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    return `${min}m ${String(sec).padStart(2, '0')}s`;
}
/** Urgency bucket used to color kitchen tickets. */
export function elapsedUrgency(fromIso, now = new Date()) {
    const elapsedMin = (now.getTime() - new Date(fromIso).getTime()) / 60_000;
    if (elapsedMin >= 10)
        return 'critical';
    if (elapsedMin >= 5)
        return 'warning';
    return 'normal';
}
/** Capitalize the first letter of each whitespace-separated word. */
export function titleCase(input) {
    return input
        .toLowerCase()
        .split(/\s+/)
        .map((w) => {
        const first = w[0];
        return first === undefined ? w : first.toUpperCase() + w.slice(1);
    })
        .join(' ');
}
/** Total quantity across all line items on an order. */
export function totalItemQuantity(order) {
    return order.items.reduce((sum, item) => sum + item.quantity, 0);
}
//# sourceMappingURL=utils.js.map